

























































































































import Vue from 'vue';
import ApiSearchPage from '@/components/apiSearch/components/ApiSearchPage.vue';
import { Grid } from '@progress/kendo-vue-grid';
import http, { handleApiResponse } from '@/services/http';
import ApiResponse from '@/models/ApiResponse';
import ApiSearch from '@/components/apiSearch/models/ApiSearch';
import { t } from "@/plugins/i18n";

type ReconMode = 'plain' | 'accno';

interface ModeTab {
  value: ReconMode;
  title: string;
  description: string;
}

interface Data {
  years: any[];
  selectedYear: string | null;
  expandedIds: number[];
  rowItems: Record<number, any>;
  tabIndex: number;
  slideClass: string;
  modeTabs: ModeTab[];
  // A rejtett hasAccno szűrő KEZDŐ értéke — szándékosan nem reaktív a fülre: a columns
  // computed nem függhet a módtól, mert az ApiSearchPage columns-watchere újraépítené az
  // ApiSearch-öt (szűrők elvesznek, stopFilterLoad elnyeli az első szűrő-változást).
  initialHasAccno: string;
}

interface Methods {
  apiSearch(): ApiSearch | undefined;
  yearFilterChange(value: string | null): void;
  markExpanded(items: any[]): any[];
  toggleExpand(item: any): void;
  editItem(item: any): void;
  createRecon(): void;
  itemsTotal(items: any[]): string;
  formatTotal(value: any): string;
}

interface Computed {
  mode: ReconMode;
  columns: any[];
  gridColumns: any[];
  itemColumns: any[];
}

export default Vue.extend<Data, Methods, Computed, {}>({
  name: 'GeneralLedgerMatching',

  components: {
    ApiSearchPage,
    Grid,
  },

  data(): Data {
    const startAccno = this.$route.query.mode === 'accno';
    return {
      years: [],
      selectedYear: String(new Date().getFullYear()),
      expandedIds: [],
      rowItems: {},
      tabIndex: startAccno ? 1 : 0,
      slideClass: '',
      modeTabs: [
        {
          value: 'plain',
          title: t("Osszepontozas_2"),
          description: t("Transactions_FokonyviSzamhozNemKotottOsszepontozas"),
        },
        {
          value: 'accno',
          title: t("Transactions_FokonyviSzammal"),
          description: t("Transactions_EgyFokonyviSzamhozKotottOsszepontozasAz"),
        },
      ],
      initialHasAccno: String(startAccno),
    };
  },

  watch: {
    tabIndex(val: number, old: number) {
      this.slideClass = val > old ? 'slide-from-right' : 'slide-from-left';

      // A másik fül lenyitott sorai ne maradjanak meg
      this.expandedIds = [];
      this.rowItems = {};

      // Az élő szűrő átírása → az ApiSearchPage deep watchere újratölt; az Év megmarad
      const search = this.apiSearch();
      if (search && search.filter) {
        (search.filter.filters.hasAccno as any).value = String(this.mode === 'accno');
      }

      // A fül az URL-ben (vissza-navigálás a szerkesztőből), history-szaporítás nélkül.
      // NEM $router.replace: a Layout router-view-ja $route.fullPath-ra kulcsolt, így a query
      // átírása újramountolná a nézetet (Év-szűrő és évlista újratöltődne). A history
      // közvetlen írása a routert nem érinti, a Vissza gomb viszont a friss URL-t olvassa.
      const href = this.$router.resolve({ query: this.mode === 'accno' ? { mode: 'accno' } : {} }).href;
      window.history.replaceState(window.history.state, '', href);
    },
  },

  async beforeMount() {
    this.years = await http
      .get<ApiResponse<any[]>>('general-ledger-recon/years')
      .then(handleApiResponse);
  },

  methods: {
    apiSearch() {
      const page = this.$refs.searchPage as any;
      return page ? (page.apiSearch as ApiSearch) : undefined;
    },

    yearFilterChange(value) {
      this.expandedIds = [];
      this.rowItems = {};

      const search = this.apiSearch();
      if (!search || !search.filter) return;

      (search.filter.filters.yearName as any).value = value || null;
    },

    markExpanded(items) {
      return items.map((item: any) => ({
        ...item,
        expanded: this.expandedIds.includes(item.id),
        ...(this.rowItems[item.id] || {}),
      }));
    },

    async toggleExpand(item) {
      const idx = this.expandedIds.indexOf(item.id);
      if (idx !== -1) {
        this.expandedIds.splice(idx, 1);
        return;
      }

      if (!this.rowItems[item.id]) {
        const result = await http
          .get<ApiResponse<any>>(`general-ledger-recon/${item.id}/items`)
          .then(handleApiResponse);

        // accSign: pozitív -> K (követel), negatív -> T (tartozik)
        const mapSign = (i: any) => ({ ...i, signLabel: i.accSign ? 'T' : 'K' });
        this.$set(this.rowItems, item.id, {
          positiveItems: result.positiveItems.map(mapSign),
          negativeItems: result.negativeItems.map(mapSign),
        });
      }

      this.expandedIds.push(item.id);
    },

    editItem(item) {
      this.$router.push(`/ledger-matching/edit/${item.id}`);
    },

    createRecon() {
      this.$router.push({
        path: "/ledger-matching/create",
        query: this.mode === 'accno' ? { withAccno: "true" } : {},
      });
    },

    itemsTotal(items) {
      return this.formatTotal((items || []).reduce((sum: number, i: any) => sum + Number(i.total || 0), 0));
    },

    formatTotal(value) {
      return new Intl.NumberFormat('hu-HU').format(Number(value || 0));
    },
  },

  computed: {
    mode(): ReconMode {
      return (this.modeTabs[this.tabIndex] || this.modeTabs[0]).value;
    },

    columns(): any[] {
      return [
      {
        cell: 'editTemplate',
        filterable: false,
        sortable: false,
        width: '110px',
      },
      {
        field: 'id',
        title: 'ID',
        width: '80px',
      },
      {
        field: 'code',
        title: t("Transactions_Pontozoszam"),
      },
      {
        field: 'remark',
        title: t("Megjegyzes"),
      },
      {
        field: 'yearName',
        title: t("Ev"),
        width: '120px',
        extraFilterValue: String(new Date().getFullYear()),
      },
      {
        field: 'accno',
        title: t("FokonyviSzam"),
      },
      {
        field: 'statusName',
        title: t("Allapot"),
        filterable: false,
      },
      {
        field: 'balance',
        title: t("Egyenleg"),
        className: 'right_text',
        filterable: false,
      },
      {
        field: 'createdBy',
        title: t("Letrehozo"),
        filterable: false,
      },
      {
        field: 'insDate',
        title: t("LetrehozasDatuma"),
        className: 'right_text',
        filterable: false,
      },
      {
        field: 'fixAssets',
        title: t("TargyiEszkoz"),
        filterable: false,
      },
      {
        filterable: false,
        width: 0,
      },
      // Rejtett szűrő: összepontozás főkönyvi számmal / anélkül (fülváltásnál az élő
      // szűrő értékét írjuk át, lásd a tabIndex watchert)
      {
        field: 'hasAccno',
        extraFilterValue: this.initialHasAccno,
      },
      ];
    },

    gridColumns(): any[] {
      return this.columns.filter((c: any) => c.field !== 'hasAccno');
    },

    itemColumns: () => [
      {
        field: 'accno',
        title: t("FokonyviSzam"),
        width: '120px',
      },
      {
        field: 'signLabel',
        title: 'T/K',
        width: '60px',
      },
      {
        field: 'total',
        title: t("Osszeg"),
        cell: 'totalCell',
      },
      {
        field: 'accDate',
        title: t("Transactions_GazdEsDatuma"),
        className: 'right_text',
        width: '130px',
      },
      {
        field: 'accEvent',
        title: t("GazdEsemeny"),
      },
    ],
  },
});
